import type { Messages } from "./types";
import { createSetupMessages } from "./setup-messages";

export const en: Messages = {
  language: {
    code: "en",
    name: "English"
  },
  common: {
    apply: "Apply",
    cancel: "Cancel",
    close: "Close",
    confirm: "Confirm",
    delete: "Delete",
    reset: "Reset",
    save: "Save",
    fused: "Fused",
    unsavedChangesConfirm: "You have unsaved changes. Leave without saving?",
    saveStatus: {
      idle: "No changes",
      pending: "Save pending",
      saving: "Saving",
      queued: "Waiting to save",
      saved: "Saved",
      error: "Save failed"
    }
  },
  tabs: {
    dashboard: "Dashboard",
    floorplan: "Floorplan",
    stats: "Stats",
    zones: "Zones",
    backup: "Manage / Backup"
  },
  setup: createSetupMessages("en"),
  api: {
    errors: {
      site_configuration_owner_required:
        "Edit the shared floorplan on its source device. This device keeps only its local radar placement.",
      site_configuration_conflict:
        "Connected devices disagree on the shared configuration. Detection continues, but shared changes are blocked until recovery completes.",
      site_configuration_continuity_unavailable:
        "The backup device is not ready with the latest shared floorplan. Detection continues, but shared changes are paused until it reconnects.",
      floorplan_manifest_invalid:
        "The shared floorplan could not be published. Check that the image and room layout were saved.",
      floorplan_delete_requires_standalone:
        "Disconnect the other NAMO devices before deleting the shared floorplan. You can replace it while they remain connected.",
      invalid_request: "The request is invalid. Check the input and try again.",
      config_not_found: "Saved configuration was not found.",
      config_write_failed: "The device could not save the configuration.",
      image_not_found: "Saved floorplan image was not found.",
      config_read_failed: "Could not read the saved floorplan configuration.",
      image_read_failed: "Could not read the saved floorplan image.",
      missing_data: "Required data is missing.",
      missing_confirm: "Confirmation is required.",
      delete_failed: "Could not delete the saved data.",
      upload_session_mismatch: "The upload session does not match. Try saving again.",
      upload_offset_mismatch: "The upload chunk order does not match. Try saving again.",
      upload_incomplete: "The upload did not complete. Try saving again.",
      upload_payload_too_large: "The upload is too large for device storage.",
      upload_storage_failed: "The device could not write the uploaded data.",
      payload_too_large: "The upload is too large for device storage.",
      api_key_unavailable: "The API key service is unavailable.",
      api_key_not_set: "No API key is saved yet.",
      api_key_unsupported: "This build does not support API key lookup.",
      api_key_random_failed: "Could not generate a secure API key.",
      api_key_save_failed: "Could not save the new API key.",
      nothing_selected: "Select at least one item to reset.",
      space_reset_failed: "Could not reset the floorplan and placement.",
      detection_reset_failed: "Could not reset detection settings.",
      connections_reset_failed: "Could not reset connected-device information.",
      stats_reset_failed: "Could not reset statistics.",
      wifi_not_ready: "Wi-Fi configuration is not ready yet.",
      wifi_unavailable: "Wi-Fi is unavailable.",
      open_wifi_unsupported: "Open Wi-Fi networks are not supported.",
      invalid_password_length: "The Wi-Fi password must be 8 to 63 characters.",
      firmware_upload_failed: "The firmware upload failed.",
      firmware_network_error: "A network error occurred during firmware upload.",
      firmware_upload_aborted: "The firmware upload was cancelled.",
      firmware_trust_unavailable: "Firmware verification is unavailable on this device.",
      firmware_header_invalid: "The firmware package header is invalid.",
      alpha_firmware_not_allowed: "Alpha firmware installation is not allowed. Enable one installation in Management/Backup first.",
      alpha_firmware_permission_invalid: "The alpha firmware permission request is invalid.",
      signed_firmware_required: "Select a signed NAMO firmware package (.namo-ota).",
      legacy_firmware_bin_required: "This older firmware requires an ESP32 OTA .bin file.",
      firmware_session_invalid: "The device returned an invalid firmware update session.",
      busy: "Another firmware update is already in progress.",
      session_mismatch: "The firmware update session no longer matches.",
      header_mismatch: "The uploaded firmware does not match the verified package.",
      trust_rejected: "The firmware signature or trust version was rejected.",
      offset_mismatch: "Firmware chunks arrived out of order.",
      size_mismatch: "The firmware package size does not match its signature.",
      expired: "The firmware upload session expired.",
      sink_begin_failed: "The firmware update partition could not be opened.",
      sink_write_failed: "Writing the firmware update failed.",
      sink_finish_failed: "Final firmware or trust verification failed."
    },
    statuses: {
      setup_wifi_connecting: "Connecting to Wi-Fi.",
      setup_wifi_connected: "Wi-Fi is connected.",
      setup_wifi_failed: "Wi-Fi connection failed.",
      ha_handoff_started: "Home Assistant handoff has started.",
      api_client_waiting: "Waiting for the native API client.",
      api_client_connected: "Native API client is connected.",
      api_client_idle: "Native API client is idle.",
      api_service_not_ready: "Native API is not ready yet."
    }
  },
  app: {
    statusWaiting: "Waiting for connection",
    configLoaded: "Configuration loaded",
    configReadFailed: (error) => `Could not read configuration. ${error}`,
    configSaved: "Settings saved",
    configSaveFailed: (error) => `Could not save settings. ${error}`,
    stateConnected: "Connected",
    stateReadFailed: (error) => `Could not read state. ${error}`,
    statsReadFailed: (error) => `Could not read statistics: ${error}`,
    systemStatusApiNotReady: "System information API is not ready.",
    controlApiNotReady: "Control API is not ready.",
    statusLedApiNotReady: "Status LED control API is not ready.",
    statusLedOn: "Status LED turned on.",
    statusLedOff: "Status LED turned off.",
    statusLedFailed: (error) => `Status LED control failed: ${error}`,
    ledDurationApiNotReady: "LED timer API is not ready.",
    ledDurationChanged: "LED auto-off time changed.",
    ledDurationFailed: (error) => `LED timer change failed: ${error}`,
    environmentCorrectionApiNotReady: "Environment correction API is not ready.",
    environmentCorrectionOn: "Environment correction turned on.",
    environmentCorrectionOff: "Environment correction turned off.",
    environmentCorrectionFailed: (error) => `Environment correction change failed: ${error}`,
    legacyPresenceFallbackOn: "Legacy detection fallback turned on.",
    legacyPresenceFallbackOff: "Legacy detection fallback turned off.",
    legacyPresenceFallbackFailed: (error) => `Legacy detection fallback change failed: ${error}`,
    temperatureOffsetApiNotReady: "Temperature offset API is not ready.",
    temperatureOffsetChanged: "Temperature offset changed.",
    temperatureOffsetFailed: (error) => `Temperature offset change failed: ${error}`,
    humidityOffsetApiNotReady: "Humidity offset API is not ready.",
    humidityOffsetChanged: "Humidity offset changed.",
    humidityOffsetFailed: (error) => `Humidity offset change failed: ${error}`,
    timezoneApiNotReady: "Timezone control API is not ready.",
    timezoneChanged: "Timezone changed. Today's statistics now start from zero.",
    timezoneApplyTimeout: "The device did not finish applying the timezone in time.",
    timezoneFailed: (error) => `Timezone change failed: ${error}`,
    floorplanSaveApiNotReady: "Floorplan save API is not ready.",
    setupModeSelectMessage: "Choose how this device will be used.",
    setupInitialChecking: "Checking initial integration status.",
    haApiWarmupMessage: "Checking the Home Assistant API connection. This may take about 1 minute.",
    haApiWarningMessage:
      "Home Assistant connection is not confirmed yet. If the device was already added, check the network and Native API settings.",
    haApiWarmupChecking:
      "The network may be briefly unstable. Checking the Home Assistant API connection.",
    haApiWaitingElapsed: (seconds) => `Waiting for the Home Assistant API connection. ${seconds}s elapsed`,
    deviceNetworkPreparing: "The device is preparing the network. Waiting for a response.",
    deviceWaitingWifiSwitch: "Waiting for the device response. Wi-Fi may briefly switch.",
    setupTitles: {
      select: "Choose usage mode",
      edge: "Finishing initial setup",
      haSetup: "Preparing Home Assistant integration",
      apiWarmup: "Waiting for Home Assistant connection",
      apiWarning: "Check Home Assistant connection"
    },
    setupBodies: {
      select:
        "Choose whether to use SmartThings Edge only or also use Home Assistant. You can switch to Home Assistant later.",
      edge: "Finishing initial setup. Wi-Fi may be briefly unstable.",
      haSetup: "Press confirm to finish Home Assistant preparation. Connection verification may take about 1 minute.",
      apiWarmup: "Home Assistant preparation is not finished yet. Press confirm to prepare the API connection.",
      apiWarning:
        "Home Assistant connection is not confirmed yet. Press confirm to prepare the API connection again, then check the device add status and API encryption key in Home Assistant."
    },
    setupContinueDashboard: "Continue to dashboard",
    setupFinishingBusy: "Finishing...",
    setupPreparingBusy: "Preparing...",
    setupConfirmContinue: "Confirm and continue",
    setupFinishingEdge: "Finishing initial setup. Wi-Fi may be briefly unstable.",
    setupFinishingHa: "Finishing network setup for Home Assistant integration.",
    setupFinishFailed: (error) => `Could not finish initial setup. ${error}`,
    edgeModeSet: "Configured for SmartThings Edge first.",
    haModeSet: "Configured to also use Home Assistant.",
    haSetupNeeded: "Home Assistant preparation is required.",
    integrationModeChangeFailed: (error) => `Could not change usage mode. ${error}`,
    setupRequestSent: "Initial setup request sent. You can keep using the dashboard.",
    haApiCheckingLong: "Checking Home Assistant API connection. This may take about 1 minute.",
    haSetupReadyNotice:
      "Device setup is complete. Home Assistant connection verification can take up to 1 minute. Check the device status in Home Assistant shortly.",
    haSetupCompleteFailed: (error) => `Could not complete integration preparation. ${error}`,
    demo: "Demo",
    haIntegrationTitle: "Home Assistant integration",
    haDocsLabel: "ESPHome Native API docs",
    haDocsSuffix: " for checking the Home Assistant connection.",
    edgeOnly: "Use SmartThings Edge only",
    haAlso: "Also use Home Assistant"
  },
  dashboard: {
    title: "Dashboard",
    description: "View sensor status and real-time detection information in one place.",
    demoMode: "Demo mode. Nothing is saved to a real device.",
    mockMode: "Viewing mock data.",
    liveMode: "View real-time position and zone settings in one place.",
    connection: "Connection",
    connected: "Connected",
    waiting: "Waiting",
    updated: "Updated",
    fusionActive: (count) => `${count}-sensor fusion`,
    fusionSending: "Fusion relay",
    fusionFallback: "Fusion pending",
    connectedRadar: "Connected radar",
    coreStatus: "Core status",
    presence: "Presence",
    motion: "Motion",
    detected: "Detected",
    notDetected: "Not detected",
    targetCount: "Targets",
    targetCountValue: (count) => `${count}`,
    targetSummary: (moving, still) => `Moving ${moving} · Still ${still}`,
    staticRadarDetected: "Assist sensor · Detected",
    staticRadarClear: "Assist sensor · Clear",
    staticRadarUnavailable: "Assist sensor · Not installed",
    environment: "Environment",
    floorplanReady: "Floorplan ready",
    radarReady: "Radar map ready",
    targetLabel: (count) => `${count} targets`,
    savedFloorplanAria: "Saved floorplan",
    floorplanLoading: "Loading floorplan",
    floorplanViewArea: "Floorplan view area",
    radarViewArea: "Radar map view area",
    floorplanLoadFailed: (error) => `Failed to load floorplan. ${error}`,
    floorplanImageLoading: "Loading saved floorplan image.",
    radarFallback: "If no floorplan is saved, the radar map appears here.",
    quickNav: "Quick navigation",
    zoneStatus: "Zone status",
    zone: "Zones",
    userZones: (count) => `User zones ${count}`,
    calibrationZones: (count) => `Correction zones ${count}`,
    filter: "Blocked",
    reduced: "Reduced",
    disabled: "Excluded",
    exit: "Exit",
    countValue: (count) => `${count}`,
    deviceStatus: "Device status",
    firmware: "Firmware",
    dashboard: "Dashboard",
    wifi: "Wi-Fi",
    storage: "Storage",
    storageUsed: (usage, percent) => `${usage} · ${percent} used`,
    uptime: "Uptime",
    platformStatus: "Platform",
    platformReady: "Ready",
    platformUnavailable: "Unavailable",
    platformMode: "Mode",
    platformStandalone: "Standalone",
    platformEnrolled: "Connected",
    nodeId: "Node ID",
    siteId: "Site ID",
    checking: "Checking",
    notConnected: "Not connected",
    wifiExcellent: "Excellent",
    wifiGood: "Good",
    wifiFair: "Fair",
    wifiWeak: "Weak",
    wifiVeryWeak: "Very weak",
    systemInfoFailed: (error) => `Failed to read system information. ${error}`,
    advancedSettings: "Advanced settings",
    collapse: "Collapse",
    expand: "Expand",
    advancedTuning: "Advanced tuning",
    advancedTuningDescription: "Less frequently used device controls are grouped here.",
    ledBehavior: "LED behavior",
    autoOff: "Auto off",
    environmentCorrection: "Environment correction",
    legacyPresenceFallback: "Legacy detection fallback",
    legacyPresenceFallbackDescription: "Use the old lambda-based presence path instead of the tracker engine. Leave this off unless you are debugging.",
    enabled: "On",
    disabledState: "Off",
    controlStatusFailed: (error) => `Failed to read control status. ${error}`,
    statusLedControl: "Status LED control",
    diagnostics: "Diagnostics",
    pairingButton: "Pair devices",
    pairing: {
      title: "Pair NAMO devices",
      description: "Securely connect nearby NAMO devices, then choose their shared floorplan and align their positions.",
      unavailable: "Secure device pairing is not available in this build or demo.",
      localDevice: "This device",
      localDeviceDescription: "Current connection status for this device.",
      state: "Pairing state",
      floorplanUsage: "Floorplan use",
      floorplanUsageStates: {
        none: "No saved floorplan",
        local: "Used only on this device",
        shared: "Sharing this device's floorplan",
        received: "Using a connected device's floorplan"
      },
      peer: "Other device",
      states: {
        idle: "Not started",
        prepared: "Waiting for the other device",
        exchanged: "Verifying the secure link",
        verified: "Code ready for confirmation",
        committed: "Securely connected · setup needed"
      },
      codeLabel: "Confirmation code",
      codeHelp: "Check that the device where you started setup shows the same code.",
      confirmed: "This device stored the secure relationship.",
      receiveTitle: "Allow another device to connect",
      receiveDescription: "Press this first so another NAMO device can find and connect to this device.",
      allowIncoming: "Allow pairing for 3 minutes",
      incomingRequestTitle: "Device connection request",
      incomingRequestDescription: "A NAMO device on this network wants to connect. Allow it only if you recognize the request.",
      incomingProgressTitle: "Connecting devices securely",
      incomingProgressDescription: "Continue the next step on the device that started the connection.",
      rejectIncoming: "Reject",
      connectTitle: "Add another device",
      ownerRequiredTitle: "Add from the configuration source",
      ownerRequiredDescription: "Adding a device changes the shared floorplan and connection list, so it must be started on the configuration source device.",
      ownerUnavailable: "The configuration source is currently offline. Try again after it reconnects or automatic takeover completes.",
      openOwnerDevice: "Open configuration source",
      connectionNotReadyTitle: "Checking connection information",
      connectionNotReadyDescription: "A new device can be added after connected devices confirm the latest shared configuration. Detection continues meanwhile.",
      resumeSetup: "Continue connection setup",
      setupResumed: "The shared configuration was delivered to the new device. Position alignment can continue.",
      nearbyDevices: "Nearby NAMO devices",
      nearbyDescription: "Already connected devices stay in the list above and are not offered again.",
      searching: "Searching nearby devices...",
      noDevices: "No compatible NAMO devices were found.",
      noDevicesDescription: "Allow pairing on the other device, then search again or enter its address manually.",
      refreshDevices: "Search again",
      manualAddress: "Enter an address manually",
      manualAddressDescription: "Use this fallback when multicast discovery is unavailable on the network.",
      address: "Other device address",
      connect: "Connect securely",
      confirm: "Confirm on this device",
      cancel: "Cancel pairing",
      connectedDevices: "Connected devices",
      connectedDescription: "NAMO devices securely connected here. They can work together after position alignment.",
      pendingSetupTitle: (count) => `${count} ${count === 1 ? "device needs" : "devices need"} setup`,
      pendingSetupDescription: (count) =>
        `${count} ${count === 1 ? "device is" : "devices are"} securely connected but not yet included in fusion because position alignment is incomplete. Finish them later in any order; adding another device now will not replace the existing connections.`,
      pendingSetupAddDescription: "Devices that still need setup remain connected. You can add this device first; each device joins fusion after its position is aligned.",
      sharedFloorplan: "Shared floorplan",
      sharedFloorplanDescription: "Devices connected here use the same image and room layout. Each device keeps its own radar position and detection zones.",
      sharedFloorplanOwner: "Device providing the floorplan",
      sharedFloorplanRevision: "Geometry revision",
      sharedFloorplanClaimOwner: "Share this device's floorplan",
      sharedFloorplanOwnerClaimed: "Connected devices will use this device's floorplan.",
      sharedFloorplanCommit: "Apply this floorplan to connected devices",
      sharedFloorplanSync: "Download the shared floorplan again",
      sharedFloorplanNotCommitted: "No floorplan selected for connected devices",
      sharedFloorplanCommitted: "Using the same floorplan",
      sharedFloorplanCommitDone: "Connected devices will use the current floorplan.",
      sharedFloorplanSyncStarted: "Authenticated floorplan sync started.",
      sharedFloorplanAutoWaiting: (seconds) => `Waiting for automatic sync slot · ${seconds}s`,
      replicationState: (state, result) => {
        if (state === "complete" && result === "ok") return "Floorplan is up to date";
        if (state === "requesting" || state === "receiving" || state === "committing") return "Updating floorplan";
        if (state === "conflict") return "Floorplan selection needs review";
        if (state === "failed") return "Could not update the floorplan";
        return "Waiting to update";
      },
      noConnectedDevices: "No connected devices",
      noConnectedDescription: "Nearby devices that allow pairing will appear below.",
      connectedCount: (count) => `${count} connected`,
      returnToMainDevice: "Return to the device managing this connection",
      membership: "State",
      readiness: "Setup",
      networkState: "Network",
      networkOnline: "Online",
      networkUnavailable: "No response",
      deviceAddress: "Address",
      deviceName: "Name",
      automaticFallback: "Automatic fallback",
      automaticFallbackAllowed: "Allowed",
      automaticFallbackNotAllowed: "Not allowed",
      automaticFallbackAllow: "Allow automatic fallback",
      automaticFallbackDisable: "Disable automatic fallback",
      automaticFallbackDescription: "Allow this device to take over coordination when another device stops responding. Each device's local presence detection continues regardless of this setting.",
      automaticFallbackEnabledNotice: "This device can now take over coordination when needed.",
      automaticFallbackDisabledNotice: "Automatic fallback was disabled for this device.",
      automaticFallbackPreparedNotice: "Position alignment is complete, and this device can now take over coordination when needed.",
      automaticFallbackPreparationFailed: "Position alignment is complete, but automatic fallback could not be enabled. Use Allow automatic fallback in the connected device list to try again.",
      singleFallbackWarning: "Only one online device can currently coordinate the connected system. Allow automatic fallback on another connected device so it can take over after a failure.",
      ready: "Ready",
      notReady: "Needs setup",
      memberships: {
        quarantined: "Connected · setup needed",
        active: "In use",
        revoked: "Removal pending"
      },
      pendingDisconnects: "Pending disconnects",
      pendingDisconnectsDescription: "These peers are blocked locally. Bilateral removal can finish when the peer responds.",
      finishDisconnect: "Finish removal",
      disconnect: "Disconnect",
      disconnectTitle: "Disconnect both devices",
      disconnectDescription: (participantId) => `Remove the relationship with ${participantId} from both devices.`,
      pendingDisconnectDescription: (participantId) => `Clean up the remaining relationship with ${participantId}.`,
      disconnectAddressHelp: "The peer must be online. Its identity is verified before either local record is removed.",
      disconnectConfirm: "Disconnect both",
      retryDisconnect: "Retry both devices",
      blockAndWait: "Block on this device",
      disconnectPendingNotice: "This device blocked the peer credential. Bilateral removal can be completed later.",
      deleteLocalRecord: "Delete local record",
      localDeleteWarning: "Deleting the local record prevents this device from cleaning up a relationship that remains on the peer.",
      localRecordDeleted: "The remaining relationship was deleted from this device.",
      disconnecting: "Disconnecting...",
      disconnected: "The relationship was removed from both devices.",
      siteMemberRemoveTitle: "Remove connected device",
      siteMemberRemoveDescription: (participantId) => `Block ${participantId}, reset its connection state to standalone, and remove it from the shared roster.`,
      siteMemberRemoveAddressHelp: "The target identity is verified before only its connection state is reset. Floorplan, radar position, zones, and tuning remain saved.",
      siteMemberRemoveConfirm: "Remove device",
      siteMemberRemoving: "Removing device...",
      siteMemberRemoved: "The target is now standalone and has been removed from the shared roster.",
      siteMemberForceRemove: "Force remove from list",
      siteMemberForceRemoveWarning: "Force removal deletes this shared roster entry even when the target could not be reset. The target may retain stale connection state. When it becomes reachable, reset device-to-device security connections under Management and backup before pairing it again.",
      siteMemberForceRemoved: "The target was forcibly removed from the shared roster.",
      leaveSite: "Leave connected site",
      leaveSiteTitle: "Leave this connected site",
      leaveSiteDescription: "The configuration owner will remove this device, then this device will restart as a standalone sensor. Floorplan, radar position, zones, and tuning remain saved.",
      leaveSiteAddressHelp: "The configuration owner must be online so it can confirm this device is no longer in the shared roster.",
      leaveSiteConfirm: "Leave site",
      leavingSite: "Leaving site...",
      leftSite: "This device was removed from the connected site and is restarting in standalone mode.",
      setup: {
        title: "Connect another NAMO device",
        description: "Follow these steps here. You only need to open the other device once to allow the connection.",
        close: "Close setup",
        currentStep: "Current step",
        steps: {
          device: "Choose device",
          select: "Choose device",
          verify: "Check code",
          floorplan: "Choose floorplan",
          complete: "Finish"
        },
        selectTitle: "Choose a device to add",
        selectDescription: "Select a nearby device, or enter its local address when automatic discovery is unavailable.",
        otherDevice: "Nearby device",
        manualAddress: "Device address",
        selectContinue: "Continue",
        waitTitle: (name) => `Allow the connection on ${name}`,
        waitDescription: (seconds) => `Waiting for up to ${seconds} seconds. You can cancel at any time.`,
        openOtherDevice: "Open the other device",
        waiting: "Waiting for permission",
        waitingHelp: "On the other device, press “Allow pairing for 3 minutes.” The rest of setup continues here.",
        verifyTitle: "Check the confirmation code",
        verifyDescription: "Continue only when the other device shows the same code.",
        code: "Confirmation code",
        codeMatches: "The codes match",
        floorplanTitle: "Choose the floorplan to share",
        floorplanDescription: "When both devices already have a floorplan, choose the room layout that both devices should use.",
        mockFloorplanRequired: "Create a floorplan first.",
        existingSiteFloorplan: "The floorplan already used by this Site will be delivered to the new device automatically.",
        sameFloorplan: "Both devices already contain the same floorplan.",
        thisFloorplan: "Use this device's floorplan",
        otherFloorplan: (name) => `Use ${name}'s floorplan`,
        noFloorplan: "No saved floorplan",
        floorplanSummary: (bytes) => `${Math.max(1, Math.round(bytes / 1024))} KB saved`,
        radarPositionKept: "Each device keeps its own radar position",
        radarPositionHelp: "Only the image and room layout are shared. Detection zones and each device's radar placement are not replaced.",
        fallbackTitle: "Prepare an automatic fallback device?",
        fallbackDescription: "Only this device can currently coordinate the connected system. Allowing the new device to take over after position alignment is strongly recommended.",
        fallbackAccept: "Prepare automatic fallback",
        fallbackDecline: "Continue without fallback",
        fallbackDeclineTitle: "Continuing without fallback is not recommended",
        fallbackDeclineDescription: "One device may have to carry several critical roles. A power or network failure can interrupt shared detection, coordinate fusion, and configuration management. Sustained resource pressure can also make the device unstable or cause it to restart.",
        fallbackGoBack: "Prepare fallback instead",
        fallbackContinueWithout: "Continue anyway",
        applying: "Applying...",
        apply: "Use this floorplan",
        completeTitle: "Devices connected",
        completeDescription: "The shared floorplan has been delivered. Align the two sensors from this device before using them together.",
        startCalibration: "Start position alignment",
        later: "Do this later",
        manageExisting: "Review existing connections on the other device",
        done: "Done",
        error: (code) => {
          if (code === "peer_manages_existing_devices") {
            return "The selected device already manages other connected devices. Review those connections on that device before moving it.";
          }
          if (code === "floorplan_required") {
            return "Create a floorplan on at least one of the two devices before connecting them.";
          }
          if (code === "site_conflict") {
            return "The selected device still belongs to another device group. Open it and review its existing connections.";
          }
          if (code === "site_configuration_owner_required") {
            return "Start the new device connection from the configuration source device.";
          }
          if (code === "owner_mismatch") {
            return "The confirmed source is no longer the configuration source. Restart setup from the current source device.";
          }
          if (code === "local_member_missing") {
            return "The new device is missing from the current Site connection list. Review the connection on the configuration source.";
          }
          if (code === "site_mismatch") {
            return "The new device still contains another Site assignment. Review its existing connections first.";
          }
          if (code === "revision_conflict") {
            return "The devices contain conflicting shared configurations that cannot be merged automatically. Review the connected devices.";
          }
          if (code === "pairing_confirmation_uncertain") {
            return "The secure connection response could not be confirmed. Saved connection records were kept; leave both devices on and try again.";
          }
          if (code === "site_state_invalid") {
            return "The selected device has incomplete connection state. Restart it and try the connection again.";
          }
          if (code === "peer_rejected") {
            return "The other device rejected the connection request.";
          }
          if (code === "site_owner_not_local") {
            return "This device could not confirm the floorplan source. Review its existing connections, then try again.";
          }
          if (code === "site_configuration_sync_timeout") {
            return "The other device did not finish floorplan synchronization in time. Check both devices' network connections, then try again.";
          }
          if (code === "site_configuration_continuity_unavailable") {
            return "The devices did not finish aligning their connection records. Leave both devices on, then try again.";
          }
          if (code === "site_configuration_conflict") {
            return "The devices report conflicting shared connection records. Review the existing connections before trying again.";
          }
          if (code === "site_reconciliation_pending") {
            return "This device is cleaning up its previous connection state. Try again shortly.";
          }
          if (code === "site_reconciliation_failed") {
            return "This device could not save the cleaned-up connection state. Restart it, then try again.";
          }
          if (code === "site_leave_owner_mismatch") {
            return "The selected device is no longer the configuration owner for this Site. Refresh the connection list and try again.";
          }
          if (code === "site_leave_owner_still_lists_node") {
            return "The configuration owner could not finish removing this device. Leave both devices online and try again.";
          }
          if (code === "group_full") {
            return "This space already has the maximum of seven connected sensing devices.";
          }
          return "Could not complete device setup. Please try again.";
        }
      },
      calibration: {
        start: "Calibrate and activate",
        preparing: "Preparing position alignment",
        recalibrate: "Recalibrate",
        title: "Align device positions",
        description: (participantId) => `Capture the same person on this device and ${participantId}, then review the correction before activation.`,
        firstPosition: "First position",
        firstPositionHelp: "Have one person stand still where both radars can see them, then capture once.",
        secondPosition: "Optional second position",
        secondPositionHelp: "Move at least 0.8 m sideways and capture again to correct rotation as well as position.",
        ready: "Correction ready",
        readyHelp: "Review the bounded correction below. Neither device's saved radar placement is rewritten.",
        steps: {
          check: "Check detection",
          first: "First position",
          second: "Second position",
          review: "Review and activate"
        },
        views: {
          together: "Together",
          reference: "This device",
          peer: "Other device"
        },
        thisDevice: "This device",
        placementRequiredTitle: "Radar placement required",
        placementRequiredDescription: (deviceNames) =>
          `Saved radar placement is missing on: ${deviceNames}. A position may appear on the floorplan before it has been saved. Open each listed device, move the radar slightly, then save.`,
        placementCheckFailed:
          "Could not verify the saved radar position. Check both devices' connections, then try again.",
        openRadarPlacement: (deviceName) => `Place radar on ${deviceName}`,
        liveStatus: "Live detection",
        liveStatusHelp: "Check that both colored points represent the same person.",
        deviceOnline: "Online",
        deviceOffline: "No response",
        targetCount: (count) => `${count} target${count === 1 ? "" : "s"}`,
        targetSeparation: (millimeters) => `Detection gap ${millimeters} mm`,
        waitingForTargets: "Capture becomes available when each device sees exactly one person.",
        mapAria: "Calibration floorplan overlaying both radar positions and detected targets",
        minimumBaseline: "Move outside this circle",
        captureFirst: "Capture snapshot",
        captureSecond: "Capture second snapshot",
        working: "Working...",
        samples: "Snapshots",
        translation: "Position correction",
        rotation: "Angle correction",
        residual: "Residual error",
        room: "Shared room",
        reset: "Start over",
        approveAndActivate: "Approve and activate",
        activated: "Both devices were calibrated and activated.",
        error: (code) => {
          if (code === "calibration_pose_missing") {
            return "A device has no saved radar position. Close calibration and save its radar placement on that device.";
          }
          if (code === "calibration_pose_contract_invalid") {
            return "The calibration position revisions are inconsistent. Close calibration and open it again.";
          }
          if (code === "calibration_scale_invalid") {
            return "Calibration failed because the saved floorplan scale is incomplete or inconsistent.";
          }
          if (code === "calibration_floorplan_mismatch") {
            return "Calibration failed because the two devices are not using the same floorplan revision.";
          }
          if (code === "calibration_membership_inconsistent") {
            return "The two devices do not agree on whether this connection is active. Review the connection state before calibrating again.";
          }
          if (code === "group_full") {
            return "This space already has the maximum of seven connected sensing devices.";
          }
          if (code === "calibration_rollback_incomplete") {
            return "Calibration stopped, but one device did not confirm that the connection was made inactive. Check both devices before retrying.";
          }
          return `Calibration failed: ${code}`;
        }
      },
      error: (code) => {
        if (code === "site_configuration_owner_required") {
          return "Start the new device connection from the configuration source device.";
        }
        if (code === "site_conflict") {
          return "This device is still responsible for another connection. Review its connected devices first.";
        }
        if (code === "pairing_confirmation_uncertain") {
          return "The other device's secure connection could not be confirmed. Existing connection records were preserved.";
        }
        if (code === "owner_mismatch") return "The configuration source changed. Try again from the current source device.";
        if (code === "local_member_missing") return "This device is not in the Site connection list and cannot receive the shared configuration.";
        if (code === "site_mismatch") return "The two devices belong to different Sites.";
        if (code === "floorplan_manifest_invalid") {
          return "Save a complete floorplan before sharing it with connected devices.";
        }
        if (code === "owner_offline") return "The device providing the floorplan is not responding.";
        if (code === "peer_not_ready") return "The other device has not allowed the connection yet.";
        if (code === "group_full") return "This space already has the maximum of seven connected sensing devices.";
        if (code === "direct_pair_not_found") return "This entry is a shared Site member, not a direct pairing record. Remove it from the configuration owner.";
        if (code === "site_member_removal_owner_changed") return "The configuration owner changed during removal. Continue from the current configuration owner.";
        if (code === "site_member_removal_superseded") return "The shared roster changed during removal. Review the current list and retry.";
        if (code === "site_member_removal_replication_pending") return "The block has not reached enough connected devices yet. Retry shortly or force-remove the roster entry.";
        if (code === "site_member_removal_site_mismatch") return "The device at this address no longer belongs to the expected shared Site, so its connections were not reset.";
        if (code === "site_member_not_canonical") return "The selected device is no longer in the shared roster. Refresh the list.";
        if (code === "site_member_revoke_incomplete") return "The removal block could not be stored. Retry the operation.";
        if (code === "site_member_reset_unconfirmed") return "The target could not be confirmed as standalone. Check that it is online, retry, or force-remove the roster entry.";
        return "Could not complete the device connection. Please try again.";
      }
    },
    changing: "Changing...",
    haMode: "Switch to HA",
    edgeMode: "Switch to Edge",
    chooseMode: "Choose mode",
    ledDialogDescription: "Choose LED behavior and the automatic off timer after presence detection.",
    turnOn: "Turn on",
    turnOff: "Turn off",
    always: "Always on",
    seconds: (seconds) => `${seconds}s`,
    minutes: (minutes) => `${minutes} min`,
    hoursMinutes: (hours, minutes) => `${hours}h ${minutes} min`,
    environmentDialogDescription: "Adjust internal correction and user offsets for temperature and humidity.",
    correctionBehavior: "Correction",
    temperatureOffset: "Temperature offset",
    humidityOffset: "Humidity offset",
    timezone: "Timezone",
    timezoneChange: "Change timezone",
    timezoneDialogDescription: "Choose the local timezone used for daily statistics.",
    timezoneSelectLabel: "Device timezone",
    timezoneUseBrowser: "Use this device's timezone",
    timezoneBrowserDetected: (timezone) => `Detected ${timezone}.`,
    timezoneBrowserUnsupported: (timezone) => `${timezone} was detected, but it is not in the supported list. Choose a timezone manually.`,
    timezoneBrowserUnavailable: "The browser timezone could not be detected. Choose a timezone manually.",
    timezoneWarningTitle: "Today's statistics will be reset",
    timezoneWarning: "Changing the timezone deletes today's counters and heatmap, then starts again from zero. Completed daily history is preserved.",
    timezoneApply: "Change and reset today",
    timezoneOptions: {
      UTC: "(UTC+00:00) Coordinated Universal Time",
      "Pacific/Honolulu": "(UTC-10:00) Honolulu",
      "America/Anchorage": "(UTC-09:00/-08:00) Anchorage",
      "America/Los_Angeles": "(UTC-08:00/-07:00) Los Angeles",
      "America/Denver": "(UTC-07:00/-06:00) Denver",
      "America/Chicago": "(UTC-06:00/-05:00) Chicago",
      "America/New_York": "(UTC-05:00/-04:00) New York",
      "America/Sao_Paulo": "(UTC-03:00) Sao Paulo",
      "Europe/London": "(UTC+00:00/+01:00) London",
      "Europe/Berlin": "(UTC+01:00/+02:00) Berlin",
      "Africa/Johannesburg": "(UTC+02:00) Johannesburg",
      "Asia/Dubai": "(UTC+04:00) Dubai",
      "Asia/Kolkata": "(UTC+05:30) Kolkata",
      "Asia/Bangkok": "(UTC+07:00) Bangkok",
      "Asia/Shanghai": "(UTC+08:00) Shanghai",
      "Asia/Seoul": "(UTC+09:00) Seoul",
      "Asia/Tokyo": "(UTC+09:00) Tokyo",
      "Australia/Perth": "(UTC+08:00) Perth",
      "Australia/Sydney": "(UTC+10:00/+11:00) Sydney",
      "Pacific/Auckland": "(UTC+12:00/+13:00) Auckland"
    },
    noSavedFloorplan: "No saved floorplan data."
  },
  zones: {
    title: "Zone settings",
    description: "Edit detection zones and false-positive correction zones on the radar map.",
    status: "Status",
    loaded: "Data loaded",
    loading: "Loading",
    zones: "Zones",
    calibration: "False-positive correction",
    detection: "Detection",
    targetDetected: (count) => `${count} targets detected`,
    count: (count) => `${count}`,
    zoneSettings: "Zone settings",
    calibrationSettings: "False-positive correction",
    staticRadarTuning: "Assist sensor sensitivity",
    staticRadarUnavailable: "LD2410C was not detected.",
    staticRadarTuningTitle: "LD2410C detection sensitivity",
    staticRadarTuningDescription: "Select a distance band and adjust its sensitivity. Live engineering mode turns off automatically when this view closes.",
    staticRadarTuningStarting: "Preparing assist sensor telemetry.",
    staticRadarGateListAria: "LD2410C distance bands",
    staticRadarGateRange: (startMeters, endMeters) => `${startMeters}-${endMeters} m`,
    staticRadarGateAria: (gate, startMeters, endMeters, moveEnergy, stillEnergy) =>
      `G${gate}, ${startMeters.toFixed(2)} to ${endMeters.toFixed(2)} meters, moving energy ${moveEnergy}, still energy ${stillEnergy}`,
    staticRadarDistance: "Detection range",
    staticRadarMovingEnergy: "Moving energy",
    staticRadarStillEnergy: "Still energy",
    staticRadarDetection: "Current state",
    staticRadarDetected: "Detected",
    staticRadarNotDetected: "Clear",
    staticRadarSensitivity: "Sensitivity",
    staticRadarApply: "Apply to selected range",
    staticRadarApplying: "Applying",
    staticRadarApplyFailed: "Could not apply the assist sensor sensitivity.",
    staticRadarLoadFailed: "Could not load assist sensor telemetry.",
    staticRadarFactoryReset: "Restore factory sensitivity",
    staticRadarFactoryResetTitle: "Reset LD2410C settings",
    staticRadarFactoryResetDescription: "This restores all LD2410C settings to factory defaults, including G0-G8 sensitivity, maximum range, absence timeout, distance resolution, and Bluetooth.",
    staticRadarFactoryResetConfirm: "Reset",
    staticRadarFactoryResetting: "Resetting",
    staticRadarFactoryResetFailed: "Could not complete the LD2410C factory reset.",
    zonePanelTitle: "Zones",
    zonesLoading: "Loading zone data.",
    zonesEmpty: "No zones are configured yet. Add a zone to create a detection, exclusion, or exit area.",
    addZone: "Add zone",
    maxZonesReached: "The maximum of 6 zones is already configured.",
    maxZonesHint: "Up to 6 detection/exclusion/exit zones can be created.",
    zoneDescription: "Define a zone, name it, exclude it from detection, or mark a likely exit path.",
    zoneName: "Zone name",
    zoneNamePlaceholder: "e.g. Bed, desk, curtain",
    deleteZone: "Delete zone",
    selectOrAddZone: "Add or select a zone.",
    zoneTypePlaceholder: "Select a zone type",
    zoneValidationTitle: "Check zone settings",
    zoneValidationInstruction: "Select Detection, Exclusion, Reduced sensitivity, Exit, or Disabled, then save again.",
    zoneValidationItem: (name) => `${name}: No zone type is selected.`,
    ownerRequiredTitle: "Edit on the source device",
    ownerRequiredDescription: "Detection zones are shared by connected devices and can only be edited on the configuration source device.",
    ownerUnavailable: "The configuration source device is currently offline.",
    openOwnerDevice: "Open source device",
    typeLabels: {
      detection: "Detection",
      filter: "Exclusion",
      reduced: "Reduced sensitivity",
      disabled: "Disabled",
      exit: "Exit"
    },
    calibrationPanelTitle: "False-positive correction",
    calibrationDescription: "Analyze repeatedly detected false-positive positions and save them as correction zones.",
    calibrationStart: "Start correction",
    calibrationStop: "Stop correction",
    calibrationStoppedByUser: "The user stopped correction.",
    calibrationActionAria: "False-positive correction action",
    delete: "Delete",
    toolbarAria: "Zone editing tools",
    undo: "Undo",
    redo: "Redo",
    convertToRect: "Convert to rectangle",
    rect: "Rectangle",
    deleteSelected: "Delete selected item",
    noneSelected: "None selected",
    calibrationZone: "Correction zone",
    calibrationZoneLabel: (index) => `Correction zone ${index}`,
    lastUpdated: "Last updated",
    radarAria: "Radar map",
    clearSelectionAria: "Clear radar selection",
    calibrationInfoAria: "Correction zone information",
    calibrationPointAria: "Correction zone vertex",
    zoneMoveAria: "Move zone",
    zoneEdgeAddAria: "Add zone vertex",
    zonePointAria: "Zone vertex",
    sceneLoadingTitle: "Preparing radar view",
    sceneLoadingDescription: "Waiting for state data.",
    zoneLabel: (index) => `Zone ${index}`,
    exitPointLabel: (index) => `Exit Point ${index}`,
    mapStatus: (count) => `${count} targets detected`,
    calibrationUnavailableNoDevice: "Available after the device is connected.",
    calibrationPirActiveCannotStart: "Cannot start while PIR motion is detected.",
    calibrationMaxZonesReached: (max) => `Up to ${max} correction zones can be saved.`,
    calibrationSavedCount: (count, max) => `Saved correction zones ${count}/${max}`,
    calibrationStaticRadarOnlyAvailable: "Spatial correction zones are full. LD2410C assist sensor correction is still available.",
    geometryMessages: {
      polygonMaxPoints: (maxPoints) => `Up to ${maxPoints} vertices can be added.`,
      polygonMinPoints: (minPoints) => `A polygon needs at least ${minPoints} vertices.`,
      zonesMaxCount: (max) => `Up to ${max} detection/exclusion/exit zones can be created.`,
      zoneMaxPoints: (maxPoints) => `Up to ${maxPoints} vertices can be added per zone.`,
      zoneMinPoints: (minPoints) => `A polygon needs at least ${minPoints} vertices.`
    },
    calibrationStatus: {
      staticRadarStarted: "Started collecting LD2410C assist radar samples",
      staticRadarFailed: "LD2410C assist radar correction could not be completed.",
      staticRadarTargetAppeared: "Correction was cancelled because an LD2450 target appeared during assist-radar-only correction.",
      staticRadarAdjusted: (gate, fromSensitivity, toSensitivity) =>
        `LD2410C G${gate} sensitivity ${fromSensitivity} -> ${toSensitivity}`,
      staticRadarStillDetected: "LD2410C still detects presence after automatic correction. Check its placement or adjust sensitivity manually.",
      staticRadarSamples: (count) => `LD2410C samples: ${count}`,
      completed: (zoneCount, gateCount) =>
        `Created ${zoneCount} correction zones and adjusted ${gateCount} LD2410C gates.`,
      noPersistentFalsePositive: "No persistent false positive required correction.",
      startRequested: "Correction start requested",
      pirPassed: "PIR condition passed",
      sampleCollectionStarted: "Started collecting target samples",
      started: "False-positive correction has started.",
      failureTitle: "Correction failed",
      stoppedTitle: "Correction stopped",
      successTitle: "Correction complete",
      noTarget: "Cannot start correction because no target is detected.",
      multipleTargets: "Cannot start correction because multiple targets are detected.",
      pirCancelled: "Correction was cancelled because PIR motion was detected.",
      multipleTargetsCancelled: "Correction was cancelled because multiple targets were detected.",
      timeout: "Correction could not create a zone because the repeated detection area is too wide or unstable.",
      candidateCreateFailed: "Could not create a false-positive correction candidate.",
      candidateCreateFailedLog: "Failed to create a false-positive correction candidate zone",
      scoreAccepted: (acceptedBy) => `Correction criteria passed: ${acceptedBy}`,
      successByScore: "The stability score threshold was passed.",
      successByRepeat: "The repeated detection area threshold was passed.",
      zoneCreated: (zoneId) => `${zoneId} zone created`,
      saved: (score) => `Correction zone saved. Stability ${score} points`,
      running: (elapsedSeconds, maxSeconds) => `Analyzing stability. ${elapsedSeconds}s / max ${maxSeconds}s`,
      waiting: "Waiting",
      waitingForTargetSample: "Waiting for target samples",
      collectingSamples: "Collecting samples",
      analyzingStability: "Analyzing stability",
      readyToCreate: "Ready to create correction zone",
      waitingWork: "Waiting for correction work",
      pirState: (motion) => `PIR state: ${motion ? "motion detected" : "no motion"}`,
      collectionFinished: "Collection time: finished",
      collectionTime: (elapsedSeconds, minSeconds) => `Collection time: ${elapsedSeconds}s / min ${minSeconds}s`,
      samples: (samples, minSamples) => `Samples: ${samples} / min ${minSamples}`,
      usedSamples: (samples) => `Used samples: ${samples}`,
      outliers: (samples) => `Excluded samples: ${samples}`,
      score: (score, threshold) => `Stability score: ${score} / ${threshold}`,
      acceptedBy: (acceptedBy) => `Accepted by: ${acceptedBy}`
    },
    calibrationDialog: {
      aria: "False-positive correction",
      title: "False-positive correction",
      runningDescription: "Collecting correction data.",
      doneDescription: "Correction has finished.",
      createdZones: (count) => `Created correction zones: ${count}`,
      adjustedStaticRadarGates: (count) => `Adjusted LD2410C gates: ${count}`,
      workItems: "Work items",
      errorLog: "Error log",
      debugLog: "Debug log",
      noLogs: "No logs recorded yet.",
      stop: "Stop correction",
      close: "Close"
    },
    protectedDialog: {
      aria: "Correction zone notice",
      title: "Correction zones are protected",
      description: "Correction zones are exclusion areas created by automatic correction. They cannot be selected or edited directly on the radar map during normal zone editing, which prevents accidental changes.",
      instruction: "To disable or delete one, select it from the false-positive correction list on the left.",
      confirm: "OK"
    },
    shrinkDialog: {
      aria: "Confirm correction zone shrink",
      title: "Correction zone minimum-size protection",
      description: "Shrinking a correction zone below its automatically guaranteed size is not recommended. Continue?",
      warning: "If you choose Yes, minimum-size protection for this correction zone will be permanently disabled, and future shrinking will not show this warning.",
      no: "No",
      yes: "Yes"
    }
  },
  stats: {
    periodToday: "Today",
    periodDays: (days) => days === 1 ? "1 day" : `${days} days`,
    recentDays: (days) => `Last ${days} days`,
    zoneLabel: (index) => `Zone ${index}`,
    filterLabel: (index) => `Filter ${index}`,
    reducedLabel: (index) => `Reduced ${index}`,
    performanceMessage: (periodLabel, hits) => `${periodLabel}: blocked or reduced ${hits} false-positive candidates.`,
    noPerformanceData: "No false-positive correction results have been collected yet.",
    noStoredDates: "No stored dates",
    noStoredConfirmedDates: "No confirmed dates are stored.",
    missingDates: (count, dates, hasMore) => `Missing ${count} days in between: ${dates.join(", ")}${hasMore ? " and more" : ""}`,
    noMissingDates: "No dates are missing in between.",
    title: "Detection stats",
    description: "Summarizes false-positive blocking, reduced handling, and zone detection counts.",
    status: "Status",
    error: "Error",
    refreshing: "Refreshing",
    dataReady: "Data loaded",
    period: "Period",
    unselected: "None selected",
    storage: "Saved",
    count: (count) => `${count}`,
    detection: "Detection",
    hits: (count) => `${count}`,
    falsePositiveCorrection: "False-positive correction",
    refresh: "Refresh",
    readFailedTitle: "Could not read statistics.",
    emptyTitle: "No statistics to show yet.",
    emptyDescription: "When false-positive correction or zone detection occurs, today's statistics will fill in first. After the date changes, they are saved as confirmed daily data.",
    periodSelect: "Select period",
    periodAria: "Statistics period",
    summaryTitle: (label) => `${label} summary`,
    selectedSummaryHint: "Aggregated values for the selected period.",
    performanceTitle: "False-positive correction results",
    blockedAndReduced: "Blocked + reduced",
    topFilterZone: "Top blocking zone",
    topReducedZone: "Top reduced zone",
    periodTrend: "Period trend",
    periodTrendHint: "Compare today through the last 30 days.",
    heatmapCellHits: (hits) => `${hits} hits`,
    noRecord: "No record",
    heatmapStatus: (label, totalHits, activeCells) => `${label} detection heatmap · total ${totalHits} hits · active cells ${activeCells}`,
    heatmapEmpty: (label) => `${label} detection heatmap · no accumulated detection positions`,
    topDetectionZones: "Top zone detections",
    noDetectionZoneHits: "No zone detection records yet.",
    topFilterZones: "Top false-positive blocking zones",
    noFilterZoneHits: "No filter records yet.",
    topReducedZones: "Top reduced zones",
    noReducedZoneHits: "No reduced handling records yet.",
    storageState: "Storage state",
    recentConfirmedDates: "Recent confirmed dates",
    storedDates: "Stored dates",
    storageRange: "Stored range",
    middleMissing: "Missing in between",
    blocked: "Blocked",
    reduced: "Reduced",
    zoneDetection: "Zone detection",
    zone: "Zone"
  },
  backup: {
    exportConfigNotReady: "Settings are not ready to export yet.",
    exportDoneMessage: "Backup file exported.",
    exportDoneStatus: "Backup file created.",
    exportFailed: (error) => `Could not create backup. ${error}`,
    importJsonOnly: "Only JSON files can be imported.",
    importJsonExtensionError: "The selected file does not have a .json extension.",
    importBlockedMessage: "This backup file cannot be imported. Check the items below.",
    importValidationFailedStatus: "Backup validation failed",
    importWarningMessage: "This backup file has warnings that need review.",
    importWarningStatus: "Backup warnings need review",
    importReadyMessage: "Backup file validation is complete. You can overwrite current settings.",
    importReadyStatus: "Backup validation complete",
    importReadFailed: (error) => `Could not read backup file. ${error}`,
    importPreparing: "Preparing backup restore.",
    importRunningStatus: "Restoring backup",
    importConfigRunning: "Saving zones and false-positive correction zones to the device.",
    importConfigDone: "Settings saved",
    importFloorplanWithImageRunning: "Restoring floorplan configuration and image to the device.",
    importFloorplanConfigRunning: "Restoring floorplan configuration to the device.",
    importFloorplanDone: "Floorplan restored",
    importStatsRunning: "Restoring statistics and heatmap history to the device.",
    importStatsUploading: (percent) => `Uploading ${percent}%`,
    importStatsDone: "Statistics restored",
    importDoneMessage: (items) => `${items.join(", ")} data imported.`,
    importDoneStatus: "Backup restore complete",
    importFailed: (error) => `An error occurred while restoring backup. ${error}`,
    importCancelledStatus: "Backup import cancelled.",
    progressConfig: "Settings",
    progressFloorplan: "Floorplan",
    progressStats: "Statistics",
    progressWaiting: "Waiting",
    itemSettings: "Settings",
    itemFloorplan: "Floorplan",
    itemStats: "Statistics",
    title: "Backup / Restore",
    description: "Save or restore zone settings, correction zones, saved floorplan, and image data as a file.",
    status: "Status",
    dataReady: "Data loaded",
    loading: "Loading",
    file: "File",
    notSelected: "Not selected",
    exportButton: "Back up",
    importButton: "Restore",
    backupFile: "Backup file",
    emptyTitle: "Create a backup file or choose a file to restore.",
    emptyDescription: "Operation results and selectable import data will appear here.",
    zones: "Zones",
    calibration: "Correction",
    filterReducedDisabled: "Filter / Reduced / Excluded",
    floorplan: "Floorplan",
    included: "Included",
    none: "None",
    statistics: "Statistics",
    dailyDays: (days) => `daily ${days} days`,
    heatmap: "heatmap",
    restoreProgress: "Restore progress",
    restoreProgressAria: "Backup restore progress",
    importData: "Data to import",
    importZonesAndCalibration: "Zone settings and false-positive correction zones",
    importFloorplanAndImage: "Floorplan and image",
    importStatsAndHeatmap: "Statistics and heatmap data",
    importErrorsTitle: "Why this cannot be imported",
    importWarningsTitle: "Items that need review",
    importConfirmDescription: "Importing overwrites only the selected data on the current device.",
    importing: "Importing",
    importWaitingForStorage: "Waiting for storage",
    importStorageBusyDescription: "Another save is still being written to the device. Restore can start after that write finishes.",
    importSelectedData: "Import selected data",
    cancel: "Cancel",
    checking: "Checking",
    waiting: "Waiting",
    checkFailed: "Check failed",
    firmware: "Firmware",
    dashboard: "Dashboard",
    copy: "Copy",
    enabled: "Enabled",
    disabledState: "Disabled",
    apiKey: "API key",
    apiKeyHidden: "API key hidden.",
    apiKeyDemoTitle: "API key is not available in demo mode.",
    apiKeyDemoMessage: "API key viewing is available only on a real device.",
    apiKeyAutoHide: (seconds) => `This window will close automatically after ${seconds} seconds.`,
    apiKeyLoadFailed: "Could not load API key.",
    apiKeyNotSet: "No API key is stored. Generate or reissue an API key.",
    apiKeyCopied: "API key copied.",
    apiKeyCopyFailed: "Could not copy. Please check it again.",
    apiKeyReveal: "Show API key",
    apiKeyRegenerate: "Reissue API key",
    apiKeyDescription: "This key is used to connect Home Assistant. Keep it private.",
    apiKeyRegenerateConfirmTitle: "Reissue the API key?",
    apiKeyRegenerateConfirmDescription: "The existing Home Assistant connection will be disconnected. Enter the new key in Home Assistant to connect again.",
    apiKeyRegenerateConfirm: "Reissue",
    apiKeyRegenerating: "Reissuing...",
    apiKeyRegeneratedTitle: "New API key issued",
    apiKeyRegenerateFailed: "Could not reissue the API key.",
    restoreDemoTitle: "Restore is disabled in demo mode.",
    restoreDemoOpenMessage: "The demo page blocks restore actions so it cannot overwrite a real device.",
    restoreDemoFileMessage: "Backup validation and restore API calls are available only on a real device.",
    restoreDemoConfirmMessage: "Selected backup data can be imported only on a real device.",
    firmwareUpdateTitle: "Firmware update",
    firmwareUpdateDescription: "Verify and install a signed NAMO firmware package on this device.",
    firmwareUpdateButton: "Firmware update",
    firmwareManualDescription: "Upload a NAMO firmware package directly to the device.",
    firmwareTarget: "Install target",
    firmwareUploading: "Uploading",
    firmwareVerifying: "Checking response",
    firmwareUpload: "Upload",
    firmwareManualUpdate: "Manual update",
    firmwareUploadingTitle: "Uploading firmware",
    firmwareVerifyingTitle: "Checking device response",
    firmwareSuccessTitle: "Update complete",
    firmwareCompletionDescription: "The new firmware was applied and the device responded normally after reboot.",
    firmwarePreviousVersion: "Previous firmware",
    firmwareInstalledVersion: "Installed firmware",
    firmwareVerifyFailedTitle: "Response check failed",
    firmwareFailedTitle: "Update failed",
    firmwareReadyTitle: "Upload ready",
    firmwareIdleTitle: "Waiting for manual update",
    firmwareUploadProgressAria: "Firmware upload progress",
    firmwareVerifyProgressAria: "Firmware response check progress",
    firmwareDoNotClose: "Do not power off the device or close the browser during the update.",
    firmwareRebootDisconnectNote: "The dashboard connection may disconnect briefly while the device reboots.",
    firmwareVerifyFailedHint: "The update may still have been applied. If the device works normally, refresh manually.",
    firmwareFailedHint: "Check the file and OTA spare space, then try again.",
    firmwareReadyHint: (fileText) => `${fileText} · Press upload to start updating the device.`,
    firmwareReadyWithOta: (otaSpace) => `Choose a NAMO firmware file. OTA spare space is ${otaSpace}.`,
    firmwareReadyWithoutOta: "Choose a NAMO firmware file. OTA spare space can be checked after system information is loaded.",
    firmwareRebooting: "Device is rebooting.",
    firmwareWaitingResponse: "Waiting for device response.",
    firmwareCheckingApplied: "Checking whether the new firmware was applied.",
    firmwareVerifyTimeout: "Could not confirm device response. Check power and network, then refresh manually.",
    firmwareAppliedReload: "Update applied. Reloading dashboard.",
    firmwareSameVersionReload: "Device responded again. The same firmware version may have been reinstalled. Reloading dashboard.",
    firmwareRespondedReload: "Device responded again. Reloading dashboard.",
    firmwareDemoTitle: "Updates are disabled in demo mode.",
    firmwareDemoOpenMessage: "Firmware upload can reboot the device, so demo mode blocks all related API calls.",
    firmwareDemoFileMessage: "The selected firmware file was not uploaded. Updates can be performed only on a real device.",
    firmwareBinOnly: "Choose a signed NAMO firmware (.namo-ota) or a development .bin file.",
    firmwareImageInvalid: "The file does not contain valid NAMO firmware metadata for ESP32.",
    firmwareFileTooLarge: (fileSize, otaSpace) => `The file is larger than the OTA spare space. ${fileSize} / ${otaSpace}`,
    firmwareApiNotReady: "Firmware upload API is not ready.",
    firmwareFileReady: "File checked. Press upload to start the update.",
    firmwareUploadingMessage: "Uploading firmware. Do not power off during the update.",
    firmwareUploadResponseLost: "Upload response was lost. Checking device response.",
    firmwareUploadInterrupted: "The upload or reboot check was interrupted. Select the firmware file and try again.",
    firmwareUpdateFailedMessage: "Firmware update failed.",
    firmwareUpdatedVersion: (before, after) => `Firmware updated: ${before} → ${after}`,
    firmwareRebuiltVersion: (version) => `Firmware ${version} was reinstalled with a newer build.`,
    firmwareReinstalledVersion: (version) => `Firmware ${version} was reinstalled successfully.`,
    alphaFirmwareAllow: "Allow alpha firmware installation",
    alphaFirmwareArmed: (seconds) => `One alpha installation allowed · within ${Math.ceil(seconds / 60)} min`,
    alphaFirmwareUnavailable: "Signed alpha installation permission is unavailable in this firmware.",
    alphaFirmwareConfirmTitle: "Allow alpha firmware?",
    alphaFirmwareConfirmDescription: "Alpha firmware is a less-tested development build. It may cause unstable behavior, configuration damage, or repeated reboots. This permits one alpha installation on this device for the next 10 minutes.",
    alphaFirmwareConfirm: "Allow once for 10 minutes",
    alphaFirmwareUpdateFailed: "Could not change alpha firmware installation permission.",
    dangerZoneTitle: "Danger zone",
    dangerZoneDescription: "Device connection, security key, and saved settings can be reset here. Create a backup first if needed.",
    rebootButton: "Restart",
    rebootDevice: "Restart device",
    rebootDemoTitle: "Restart is disabled in demo mode.",
    rebootDemoMessage: "The restart API can be called only on a real device.",
    rebootRequestedTitle: "Restart requested",
    rebootRequestedMessage: (seconds) => `The device will restart in about ${seconds} seconds.`,
    rebootFailedTitle: "Restart failed",
    rebootFailedMessage: "An error occurred while restarting.",
    rebootConfirmTitle: "Restart the device?",
    rebootConfirmDescription: "Press confirm to restart the device.",
    rebooting: "Restarting...",
    resetButton: "Reset",
    resetDemoTitle: "Reset is disabled in demo mode.",
    resetDemoMessage: "The reset API can be called only on a real device.",
    resetSelectionRequiredTitle: "Select reset scope",
    resetSelectionRequiredMessage: "Select at least one item to reset.",
    resetRebootSuffix: (seconds) => ` The device will reboot in about ${seconds} seconds to finish setup.`,
    resetRequestedTitle: "Reset requested",
    resetRequestedMessage: (rebootSuffix) => `Selected items were reset.${rebootSuffix}`,
    resetFailedTitle: "Reset failed",
    resetFailedMessage: "An error occurred while resetting.",
    resetScopeTitle: "Reset scope",
    resetScopeDescription: "Press reset, choose items below, then confirm in the final dialog.",
    resetItemsTitle: "Items to reset",
    resetSelectAll: "Select all",
    resetSpaceData: "Floorplan image and room layout, furniture placement, and radar position",
    resetDetectionData: "User detection areas, false-positive correction areas, excluded or reduced areas, and environment and general settings",
    resetConnectionsData: "Connected devices, secure connections, position alignment and fusion, automatic replacement, and shared space information",
    resetWifiData: "Saved Wi-Fi information",
    resetStatsData: "Statistics history",
    resetSelected: "Reset selected items",
    resetIdleTitle: "Waiting for reset",
    resetIdleDescription: "Press the reset button on the left to choose the reset scope.",
    resetConfirmTitle: "Proceed with reset?",
    resetConfirmDescription: "Selected data will be deleted. If settings data or Wi-Fi reset is included, the device will reboot after sending the result.",
    resetConfirmButton: "Proceed with reset",
    resetting: "Resetting...",
    bootGuardHint: (seconds) => `Initial boot safety check is running. Available in ${seconds} seconds.`,
    bootGuardTitle: "Boot stabilization in progress",
    bootGuardNotice: (action, seconds) =>
      `${action} is unavailable for ${seconds} seconds after boot. Try again after OTA rollback detection and device initialization are complete.`,
    wifiNotConnected: "Not connected",
    wifiExcellent: "Excellent",
    wifiGood: "Good",
    wifiFair: "Fair",
    wifiWeak: "Weak",
    wifiVeryWeak: "Very weak",
    uptimeDaysHours: (days, hours) => `${days}d ${hours}h`,
    uptimeHoursMinutes: (hours, minutes) => `${hours}h ${minutes}m`,
    uptimeMinutes: (minutes) => `${minutes}m`,
    systemInfoTitle: "System information",
    systemInfoDescription: "Check ESP32 memory, storage, and wireless connection status.",
    flashOccupancy: "Flash occupancy",
    systemCheckingDescription: "Checking device status.",
    systemSnapshotDescription: "Values are read once when entering this tab.",
    systemReadFailed: "Could not read system information",
    systemSection: "System",
    uptime: "Uptime",
    storageSection: "Storage",
    usedWithPercent: (usage, percent) => `${usage} · ${percent} used`,
    firmwareSpace: "Firmware space",
    otaSpace: "OTA spare space",
    storageData: "Stored data",
    settingsStats: "Settings / statistics",
    internalRam: "Internal RAM",
    minInternalRamFree: "Lowest free internal RAM",
    externalRam: "External RAM",
    networkSection: "Network",
    systemWaitingTitle: "Waiting for system information",
    systemWaitingDescription: "Device status is checked once when entering the Management / Backup tab.",
    issueLine: (line) => `line ${line} · `,
    issueMessages: {
      backup_json_parse_failed: (params) =>
        params.column ? `Could not read the JSON file near column ${params.column}.` : "Could not read the JSON file.",
      backup_root_not_object: () => "The top-level value of the backup file must be an object.",
      backup_app_mismatch: (params) => `This may not be a backup file created by this app. Expected ${params.expected}.`,
      backup_format_version_unsupported: (params) => `Unsupported backup format version. Supported version is ${params.supported}.`,
      backup_created_at_invalid: () => "The backup creation time is missing or invalid.",
      backup_checksum_missing: () => "The checksum is missing or has an invalid format. The file may have been edited manually.",
      backup_checksum_mismatch: () => "The checksum does not match. The backup file may have been edited manually or corrupted.",
      backup_config_not_object: () => "The config value must be an object.",
      backup_config_version_invalid: () => "config.version must be a number.",
      backup_config_zones_not_array: () => "The software zone list must be an array.",
      backup_config_zones_too_many: (params) => `Software zones can import up to ${params.max} items. This file has ${params.actual}.`,
      backup_calibration_zones_not_array: () => "The false-positive correction zone list must be an array.",
      backup_calibration_zones_too_many: (params) =>
        `False-positive correction zones can import up to ${params.max} items. This file has ${params.actual}.`,
      backup_config_floorplan_not_object: () => "The floorplan value is not an object, so it will not be imported.",
      backup_floorplan_not_object: () => "The floorplan backup value is not an object, so it will not be imported.",
      backup_floorplan_document_invalid: () => "The floorplan configuration document is missing or invalid, so it will not be imported.",
      backup_floorplan_image_not_object: () => "The floorplan image backup value is not an object, so it will not be imported.",
      backup_floorplan_image_incomplete: () => "The floorplan image backup is incomplete. mime, bytes, and dataBase64 are required.",
      backup_stats_not_object: () => "The statistics backup value is not an object, so it will not be imported.",
      backup_stats_today_invalid: () => "Today's statistics value is not an object, so it will be left empty.",
      backup_stats_daily_invalid: () => "The daily statistics value is not an array, so it will be left empty.",
      backup_stats_heatmap_invalid: () => "The heatmap value is not an object, so it will not be imported.",
      backup_zone_not_object: () => "Zone information must be an object.",
      backup_zone_id_invalid: (params) =>
        params.kind === "calibration"
          ? `Correction zone ids must use calibration_1 through calibration_${params.max}.`
          : `Software zone ids must use zone_1 through zone_${params.max}.`,
      backup_zone_id_out_of_range: (params) =>
        params.kind === "calibration"
          ? `${params.id} is not supported. Correction zones support calibration_1 through calibration_${params.max}.`
          : `${params.id} is not supported. Software zones support zone_1 through zone_${params.max}.`,
      backup_zone_id_duplicate: (params) => `${params.id} is duplicated.`,
      backup_calibration_polygon_unsupported: () => "Correction zones currently can only import rectangles.",
      backup_min_size_unlocked_invalid: () => "minSizeUnlocked is not a boolean, so it will be treated as false.",
      backup_zone_name_not_string: () => "The zone name must be a string.",
      backup_zone_name_too_long: (params) => `Zone names can use up to ${params.max} characters including spaces. Current length is ${params.actual}.`,
      backup_zone_type_invalid: (params) => `The zone type is invalid. Allowed values are ${params.allowed}.`,
      backup_zone_shape_invalid: () => "The zone shape is invalid. Only rect or polygon can be used.",
      backup_zone_points_not_array: () => "points must be an array.",
      backup_zone_rect_points_invalid: (params) => `Rectangle zones must have exactly 4 vertices. Current count is ${params.actual}.`,
      backup_zone_polygon_points_invalid: (params) =>
        `Polygon zones need ${params.min} to ${params.max} vertices. Current count is ${params.actual}.`,
      backup_zone_point_invalid: () => "Each vertex must be in [x, y] format.",
      backup_zone_point_number_invalid: () => "x and y coordinates must be numbers.",
      backup_zone_point_integer_invalid: () => "x and y coordinates must be integer mm values.",
      backup_zone_point_x_out_of_range: (params) => `x is out of range: ${params.value}. Allowed range is ${params.min} to ${params.max}mm.`,
      backup_zone_point_y_out_of_range: (params) => `y is out of range: ${params.value}. Allowed range is ${params.min} to ${params.max}mm.`,
      backup_zone_points_all_zero: () => "All vertices are (0, 0), so this zone cannot be imported.",
      backup_zone_area_empty: () => "The zone width or height is 0, so it must have a drawable area."
    }
  },
  floorplan: {
    candidateList: {
      title: "Room list",
      noName: "Unnamed",
      confidence: (confidence) => `Confidence ${confidence}%`,
      approximateSize: (widthPx, heightPx, widthMm, heightMm) =>
        `${widthPx} x ${heightPx}px · about ${widthMm} x ${heightMm}mm`,
      roomNamePlaceholder: "Room name",
      estimatedSize: "Estimated size",
      estimatedPixelBasis: (widthPx, heightPx) => `Based on ${widthPx} x ${heightPx}px`,
      width: "Width",
      height: "Height",
      noCandidates: "No rooms have been created yet.",
      status: {
        candidate: "Candidate",
        confirmed: "Confirmed",
        rejected: "Excluded"
      },
      notes: {
        widthFromOuter: "Based on full width",
        heightFromOuter: "Based on full height",
        manuallyEdited: "Edited manually"
      },
      inline: {
        snapActive: "Snapping this room to walls on the floorplan.",
        splitActive: "Splitting this room on the floorplan.",
        mergeSelected: "Selected for merging.",
        mergePickMore: "Select one more room to merge."
      },
      actions: {
        snap: "Snap to walls",
        split: "Split room",
        merge: "Merge rooms",
        confirm: "Confirm",
        reject: "Exclude",
        remove: "Delete"
      }
    },
    ocrDialog: {
      busyTitle: "OCR analysis running",
      errorTitle: "Check OCR analysis",
      doneTitle: "OCR analysis complete",
      progressAria: "OCR progress",
      cancelLabel: "Cancel task",
      cancelAction: "Stop OCR",
      closeLabel: "Close",
      closeAction: "Review results"
    },
    editTools: {
      title: "Editing tools",
      split: "Split room",
      merge: "Merge rooms",
      snap: "Snap to walls",
      door: "Add door"
    },
    radarStep: {
      title: "Radar placement",
      description: "Prepare radar placement from the full floorplan size and the cyan outer reference box.",
      totalSize: "Total size",
      referenceBox: "Reference box",
      scale: "Scale",
      scaleFineTune: "Fine scale adjustment",
      zoomOutAria: "Zoom radar map out",
      zoomInAria: "Zoom radar map in",
      scaleHint: "Adjustment is limited to 95-105% of the automatic scale.",
      nextWork: "Next: set the radar position and direction on the floorplan.",
      advancedOptions: "Advanced options",
      occlusionTitle: "Radar occlusion exceptions",
      occlusionDescription: "Exclude only physical walls that the radar can actually see through.",
      occlusionEditOn: "Edit occlusion exceptions",
      occlusionEditOff: "Finish editing exceptions",
      occlusionActiveHint: "Click a physical wall on the floorplan to toggle its occlusion behavior.",
      occlusionIgnored: (count) => `${count} occlusion exceptions`,
      occlusionReset: "Reset all exceptions",
      previousStep: "Previous step",
      roomsScale: "Room names / scale",
      nextStep: "Next step",
      finalReview: "Final review"
    },
    finalStep: {
      unavailable: "Not ready",
      title: "Final review",
      description: "Nothing has been saved to the ESP32 yet. Review the settings prepared so far.",
      image: "Image",
      config: "Config",
      roomCandidates: "Room candidates",
      roomCount: (count) => `${count}`,
      totalSize: "Total size",
      inputRequired: "Input required",
      radarPosition: "Radar position",
      rotation: "Rotation",
      occlusionExceptions: "Occlusion exceptions",
      edgeCount: (count) => `${count}`,
      jsonSize: "JSON size",
      checklistImage: "The uploaded image is prepared as a WebP candidate for ESP32 storage.",
      checklistRooms: "Room candidates and OCR room-name results are included in the config candidate.",
      checklistScale: "Radar scale is calculated from the full floorplan size.",
      checklistRadar: "Radar position, rotation, and occlusion exceptions are included in the save candidate.",
      cannotBuildJson: "The save candidate JSON cannot be built yet. Check the image and total-size inputs.",
      previousStep: "Previous step",
      radarPlacement: "Radar placement",
      saving: "Saving",
      save: "Save",
      saveAction: "Save"
    },
    debug: {
      count: (count) => `${count}`,
      times: (count) => `${count}`,
      chars: (count) => `${count} chars`,
      none: "None",
      selectedState: "Selected",
      rejectedState: "Rejected",
      ocrDebug: "OCR debug",
      showOcrRegions: "Show OCR regions",
      ocrNotRun: "OCR has not been run yet.",
      recognizedWords: "Recognized words",
      recognizedLines: "Recognized lines",
      recognizedBlocks: "Recognized blocks",
      rawPieces: "Raw pieces",
      mergedPieces: "Merged pieces",
      wallBlockedMerges: "Wall-blocked merges",
      textCandidates: "Text candidates",
      ocrCrops: "OCR crops",
      removedGuideLines: "Removed guide lines",
      rotatedOcr: "Rotated OCR",
      emptyCrops: "Empty crops",
      roomNameCandidates: "Room-name candidates",
      noiseCandidates: "Noise candidates",
      fullText: "Full text",
      preview: "Preview",
      confidence: (confidence) => `confidence ${confidence}%`,
      analysisLog: "Analysis log",
      roomCandidateDebug: "Room candidate debug",
      showFloorplanDebug: "Show floorplan debug",
      showWallMaskOverlay: "Show wall detection regions",
      analysisMethod: "Analysis method",
      candidatesBeforeCleanup: "Candidates before cleanup",
      finalCandidates: "Final candidates",
      candidateFilter: "Candidate filter",
      filterOff: "Off",
      filterOn: "On",
      edgeAnalysisDetail: "Edge analysis detail",
      colorFillAnalysisDetail: "Color/fill analysis detail",
      wallLineAnalysisDetail: "Wall-line analysis detail",
      cellSize: "Cell size",
      analysisGrid: "Analysis grid",
      totalCells: "Total cells",
      edgePixels: "Edge pixels",
      wallCandidateCells: "Wall candidate cells",
      expandedWallCells: "Expanded wall cells",
      freeSpaceRegions: "Free-space regions",
      cannyThreshold: "Canny threshold",
      colorPixels: "Color pixels",
      colorCandidateCells: "Color candidate cells",
      connectedRegions: "Connected regions",
      darkPixels: "Dark pixels",
      wallSelectionDebug: "Wall selection debug",
      wallCellCoordinates: "Wall cell coordinates",
      rejectedCandidates: "Rejected candidates",
      saveWallSelectionDebugTxt: "Save wall selection debug txt",
      wallSelectionDebugNote: "Selected and rejected coordinates are written to the txt file.",
      wallSnapDebug: "Wall snap debug",
      checkedEdges: "Checked edges",
      notAxisAligned: "Not axis-aligned",
      candidateQueries: "Candidate queries",
      noRedCandidateLine: "No red candidate line",
      alreadyAligned: "Already aligned",
      moveAttempts: "Move attempts",
      wallSnapApplied: "Wall snap applied",
      selfIntersectionRejected: "Self-intersection rejected",
      overExpansionRejected: "Over-expansion rejected",
      cornerAttempts: "Corner attempts",
      cornerApplied: "Corner applied",
      noIntersection: "No intersection",
      nearExistingPoint: "Near existing point",
      spikeCornerRejected: "Spike corner rejected",
      cornerSelfIntersection: "Corner self-intersection",
      wallSnapLog: "Wall snap log",
      initialCandidateCells: "Initial candidate cells",
      expandedCells: "Expanded cells",
      rejectedTooSmallBlob: "Rejected too-small blobs",
      rejectedTooSmallArea: "Rejected too-small area",
      rejectedTooLargeArea: "Rejected too-large area",
      rejectedSparse: "Rejected sparse regions",
      rejectedThin: "Rejected too-thin regions",
      imageLine: (width, height) => `Image: ${width} x ${height}`,
      gridLine: (width, height, cellSize) => `Grid: ${width} x ${height} / cell ${cellSize}px`,
      selectedWallCellsLine: (count) => `Selected wall cells: ${count}`,
      rejectedWallCellsLine: (count) => `Rejected wall candidate cells: ${count}`,
      selectedReasonCountsHeader: "[Selected reason counts]",
      rejectedReasonCountsHeader: "[Rejected reason counts]",
      selectedWallCellsHeader: "[Selected wall cells]",
      rejectedWallCandidateCellsHeader: "[Rejected wall candidate cells]",
      reasonCountLine: (reason, count) => `${reason}: ${count}`
    },
    panel: {
      selectImageStatus: "Select a floorplan image.",
      uploadThenAnalyze: "After uploading a floorplan, room detection can run automatically.",
      canUseEsp32Candidate: "This can be used as an ESP32 storage candidate.",
      canAnalyzeRooms: "Room auto-detection is ready.",
      unsupportedImageType: "Only PNG, JPG, and WebP images can be uploaded.",
      imageTooLarge: "The image file is too large. Use a file up to 5 MB.",
      imageTooSmall: "The image is too small. Use at least 800px on the long side and 500px on the short side.",
      webpTooLarge: "Could not convert to an 80 KB or smaller WebP while preserving OCR quality.",
      canvasUnavailable: "The image cannot be converted because Canvas is unavailable.",
      webpFailed: "WebP conversion failed.",
      autoDetectingRooms: "Automatically detecting rooms from wall lines.",
      autoAnalysisStarted: "Room auto-detection started.",
      analysisCanvasFailed: "Could not prepare Canvas for image analysis.",
      pixelDataReady: "Image pixel data is ready.",
      wallAnalysisRunning: "Running wall-line analysis.",
      roomCandidatesFound: (count) => `Found ${count} room candidates.`,
      noRoomCandidates: "No room candidates were found.",
      autoAnalysisComplete: (count) => `Room auto-detection complete: ${count}`,
      error: (message) => `Error: ${message}`,
      stepTitle: {
        image: "Step 1 · Prepare image",
        rooms: "Step 2 · Detect rooms",
        ocr: "Step 3 · Room names / scale",
        radar: "Step 4 · Radar placement",
        final: "Step 5 · Final review"
      },
      stepDescription: {
        image: "Upload a PNG/JPG/WebP file to create a WebP save candidate in browser memory. Nothing is saved to the ESP32 yet.",
        rooms: "Review automatically found room candidates and refine them with add, split, and merge tools.",
        ocr: "Review room names and enter the full floorplan width and height.",
        radar: "Prepare radar placement from the cleaned rooms and full scale.",
        final: "Review the image, room candidates, and radar placement before saving to the ESP32."
      },
      saveCannotBuild: "The save candidate cannot be built. Check the image and total-size inputs.",
      saveApiUnavailable: "The floorplan save API is not ready.",
      saveInProgress: "Saving the floorplan image and configuration to the ESP32.",
      saveDone: "Saved the floorplan image and configuration.",
      storedSizeRequired: "Enter both the full width and height before saving.",
      storedSaveInvalid: "Cannot save. Check the full width and height.",
      sizeRequired: "Enter the full floorplan width and height to calculate room sizes.",
      roomSizesCalculated: (count) => `Calculated sizes for ${count} rooms.`,
      roomSizeHint: "Enter the full floorplan width and height to estimate room sizes.",
      preciseSizeHint: "Open fine tuning to see estimated room sizes and manual correction fields.",
      ocrNotRun: "OCR has not been run yet.",
      ocrPreparing: "Preparing OCR analysis.",
      ocrStarted: "OCR analysis started.",
      ocrJob: {
        cancelled: "OCR analysis was cancelled.",
        engineLoading: "Loading the OCR engine.",
        detectingText: "Finding text candidate areas.",
        noTextRegions: "No text candidate areas were found.",
        preparingWorker: (regions) => `Preparing the OCR worker. ${regions} OCR regions will be recognized.`,
        recognizingRegion: (index, total) => `Recognizing text candidate ${index}/${total}.`,
        recognizingRegionRotation: (index, total, rotationIndex, rotations) =>
          `Recognizing text candidate ${index}/${total}. (${rotationIndex}/${rotations})`,
        mergingResults: "Cleaning up OCR results.",
        done: "OCR analysis is complete.",
        tesseractMissing: "Tesseract.js loaded, but the OCR object was not found.",
        tesseractCdnFailed: "Could not load Tesseract.js from the CDN.",
        imageDataFailed: "Could not prepare image data for OCR.",
        cropCanvasFailed: "Could not create the OCR crop image.",
        cropScaleFailed: "Could not create the enlarged OCR crop image.",
        cropPngFailed: "Could not create the OCR crop PNG.",
        cropRotateFailed: "Could not create the rotated OCR crop image.",
        statusLoadingCore: "Loading OCR core.",
        statusInitializingEngine: "Initializing OCR engine.",
        statusLoadingLanguage: "Loading Korean/English training data.",
        statusInitializingApi: "Initializing OCR API.",
        statusRecognizingText: "Recognizing text.",
        statusWorking: "Processing OCR work."
      },
      ocrUseOriginal: "Using the original uploaded image as OCR input.",
      ocrUseWebp: "Using the WebP save candidate as OCR input.",
      ocrMatches: (count) => `Matched ${count} room-name candidates to room areas.`,
      ocrNoMatches: "No room-name candidates could be applied inside room areas.",
      ocrDone: "OCR analysis is complete.",
      ocrDoneSummary: (textCandidates, regions, blocks, lines, words) =>
        `OCR complete: ${textCandidates} text candidates, ${regions} crops, ${blocks} blocks, ${lines} lines, ${words} words found.`,
      ocrCancelled: "OCR analysis was cancelled.",
      ocrCancelledByUser: "The user cancelled OCR analysis.",
      ocrError: "An error occurred during OCR analysis.",
      ocrCanceling: "Cancelling OCR analysis.",
      ocrCancelRequested: "OCR cancellation was requested.",
      previousStep: "Previous step",
      nextStep: "Next step",
      firstStep: "This is the first step",
      imageReady: "Prepare image",
      roomAutoDetect: "Detect rooms",
      roomNameRecognition: "Recognize room names",
      roomCandidateCleanup: "Clean up room candidates",
      radarPlacement: "Radar placement",
      goToOcrTitle: "Go to the room-name recognition step.",
      goToRadarTitle: "Go to the radar placement step.",
      goToRadarDisabledTitle: "Enter both the full floorplan width and height.",
      analyzing: "Analyzing",
      clearImage: "Clear image",
      converting: "Converting",
      selectImage: "Select image",
      drawFloorplan: "Draw floorplan",
      drawFloorplanHint: "Draw the full outer boundary, then split it into rooms.",
      manualFloorplanName: "Manually drawn floorplan",
      manualOutlineRequired: "Finish the outer boundary before continuing.",
      manualRoomRequired: "Add at least one room before placing the radar.",
      manualOutlineTitle: "Draw the full outer boundary",
      manualOutlineHint: "Mark the outside corners of the entire home. Points snap to the 0.5 m grid and existing corner lines, while lengths are shown to 0.1 m.",
      manualOutlineCancelled: "The unfinished boundary was cleared. Start again when you are ready.",
      manualStartOutline: "Draw outer boundary",
      manualOutlineNeedPoints: "Click at least three outside corners of the entire home.",
      manualOutlineReady: "Check the closing edge, then press Done. Rooms are added separately in the next step.",
      manualOutlineInvalid: "The boundary cannot cross itself or reuse the same corner. Adjust the points before finishing.",
      manualRoomsTitle: "Add rooms inside the boundary",
      manualRoomsHint: "The outer boundary is ready. Add each room as a separate polygon inside it.",
      manualRoomsReady: (count) => `${count} room${count === 1 ? "" : "s"} added. Add another room or continue to radar placement.`,
      manualAddRoom: "Add room",
      manualEditOutline: "Edit outer boundary",
      manualOutlineEditTitle: "Edit outer boundary",
      manualOutlineEditHint: "Drag corners, click an edge to add a corner, or double-click a corner to remove it.",
      manualOutlineEditReady: "The boundary and every room are valid. Press Done to keep these changes.",
      manualOutlineEditInvalid: "Keep every room inside a simple outer boundary before finishing.",
      manualRoomInvalid: "Keep the room inside the outer boundary and make sure its edges do not cross.",
      manualFloorplanLocked: "The saved drawing is locked. Delete this floorplan and draw it again to change its boundary or rooms.",
      manualDoorsTitle: "Doors",
      manualDoorType: "Door type",
      manualAddDoor: "Add door",
      manualHingedDoor: "Hinged door",
      manualSlidingDoor: "Sliding door",
      manualDoorPlacementTitle: "Place doors on walls",
      manualDoorPlacementHint: "Move near a wall to snap the door into place. Drag the door along the same wall, or drag either end to resize it. Saving registers the opening as an exit point.",
      manualDoorWallRequired: "That point is too far from a wall. Click the wall where the door should go.",
      manualDoorWallTooShort: "The selected wall is too short for this door.",
      manualDoorOverlap: "Another door already occupies this position. Choose a clear section of wall.",
      manualNoDoors: "No doors have been added yet.",
      manualDoorExitZoneLimit: "There is not enough room to register every door as an exit point. Up to six software zones, including exit points, are supported.",
      manualDoorExitSaveUnavailable: "The door exit points cannot be saved right now. Try again shortly.",
      manualDeleteDoor: "Delete door",
      manualPlaceDoorAria: "Place a door on a wall",
      statusPassed: "Passed",
      statusCheckRequired: "Check required",
      statusWaiting: "Waiting",
      saveReady: "Ready to save",
      savingShort: "Saving",
      totalFloorplanSize: "Full floorplan size",
      totalFloorplanSizeDescription: "Enter the full width and height from the cyan outer reference box.",
      totalFloorplanSizeEditDescription: "Edit the full width and height from the cyan outer reference box.",
      totalWidth: "Full width",
      totalHeight: "Full height",
      preciseTuning: "Fine tuning",
      closePreciseTuning: "Close fine tuning",
      limitations: "Requirements",
      limitationsDescription: "Use at least 800px on the long side and 500px on the short side. The final WebP candidate must be 80 KB or smaller.",
      workspaceAria: "Floorplan workspace",
      editToolsAria: "Floorplan editing tools",
      storedEditToolsAria: "Saved floorplan editing tools",
      undoTitle: "Undo (Ctrl+Z)",
      redoTitle: "Redo (Ctrl+Y)",
      scaledRadarMapTitle: "Show scaled radar map",
      radarMapTitle: "Show radar map",
      radarMap: "Radar map",
      finish: "Done",
      cancel: "Cancel",
      uploadedCandidateAria: "Uploaded floorplan WebP candidate",
      roomCandidateLayerAria: "Room candidate area",
      addSplitPointAria: "Add room split point",
      addManualRoomPointAria: "Add manual room point",
      zoomInTitle: "Zoom in",
      zoomOutTitle: "Zoom out",
      resetZoomTitle: "Reset zoom",
      toolTitleSnap: "Snap to walls",
      toolTitleSplit: "Split room",
      toolTitleMerge: "Merge rooms",
      toolTitleManualRoom: "Add room",
      snapSelectWall: "Select a wall line to snap to, then press Done.",
      snapSelectEdge: "Select the room edge to align.",
      splitStart: "Click the start point of the split line inside the room.",
      splitEnd: "Click the end point. It will align horizontally or vertically.",
      splitReady: "Press Done to split the room into two rooms.",
      splitInvalid: "The split line does not divide the room correctly. Adjust the points and try again.",
      mergeSelectTwo: "Select two rooms to merge.",
      mergeSelectOneMore: "Select one more room to merge.",
      mergeReady: "Press Done to merge the selected two rooms.",
      manualNeedPoints: "Click at least three room corner points on the floorplan.",
      manualReady: "Press Done to add the new room. You can still add more corner points.",
      manualInvalid: "Adjust the points so the polygon is valid before finishing.",
      defaultRoomName: (index) => `Room ${index}`,
      defaultRoomCandidateName: (index) => `Room candidate ${index}`,
      storedLoading: "Loading",
      storedTitle: "Saved floorplan",
      storedDescription: "Saved floorplan data was found on the ESP32.",
      storedState: "Status",
      storedDataReady: "Data ready",
      storedMode: "Mode",
      storedModeView: "View",
      storedModeEdit: "Edit",
      storedFloorplan: "Floorplan",
      storedReady: "Ready",
      storedRooms: "Rooms",
      storedRoomCount: (count) => `${count}`,
      storedView: "View",
      storedEdit: "Edit",
      storedCreateNew: "Create new",
      storedReplace: "Replace floorplan",
      storedDelete: "Delete floorplan",
      storedReplaceCancel: "Cancel replacement",
      floorplanOwnerRequiredTitle: "Change the floorplan on the configuration owner",
      floorplanOwnerRequiredDescription: "This floorplan is shared by connected NAMO devices. Open the configuration owner to replace or manage it.",
      floorplanOpenOwner: "Open configuration owner",
      floorplanOwnerUnavailable: "The configuration owner's address is not available. Check that device's network connection.",
      floorplanDeleteConnectedTitle: "Disconnect devices before deleting the floorplan",
      floorplanDeleteConnectedDescription: "A connected Site needs its floorplan for shared coordinates and fusion. You can replace it now, or disconnect the other devices before deleting it.",
      floorplanRadarRelocatedTitle: "Some radar positions need to be set again",
      floorplanRadarRelocatedDescription: (count) => `${count} radar position${count === 1 ? " was" : "s were"} outside the new coordinate frame and moved to its center. Open each device and set its radar position again.`,
      floorplanRadarRelocatedDevice: (address) => `Open ${address}`,
      floorplanRadarRelocatedUnavailable: "This device is offline or has no known address.",
      storedSaving: "Saving",
      storedSaveChanges: "Save changes",
      storedSave: "Save",
      storedRoomEdit: "Edit rooms",
      storedRadarPosition: "Change radar position",
      storedZoneEdit: "Edit zones",
      storedCalibrationEdit: "Edit correction zones",
      storedFurniture: "Furniture layout",
      storedScaleRecalculated: "Recalculated the full size and estimated room dimensions.",
      storedSaveInProgress: "Saving floorplan configuration to the ESP32.",
      storedSaveDone: "Saved floorplan configuration.",
      storedSaveComplete: "Saved",
      storedAddZoneUnavailable: "A zone cannot be added.",
      storedZoneAdded: "Added a zone.",
      storedZoneDeleted: "Deleted the zone.",
      storedZoneRectified: "Converted the zone to a rectangle.",
      storedAddPointUnavailable: "A point cannot be added.",
      storedZonePointAdded: "Added a zone point.",
      storedCalibrationUpdated: "Updated the correction area.",
      storedZoneUpdated: "Updated the zone.",
      storedZonePointDeleted: "Deleted the zone point.",
      storedFurnitureChanged: "Changed the furniture layout.",
      storedFurnitureAdded: "Added furniture.",
      storedFurnitureRotated: "Rotated furniture.",
      storedFurnitureDeleted: "Deleted furniture.",
      storedFurnitureLabel: (id, fallback) => ({
        bed: "Bed",
        desk: "Desk",
        dining_table: "Dining table",
        sofa: "Sofa",
        tv_console: "TV console"
      })[id] ?? fallback,
      storedZoneEditorTitle: (source) => source === "calibration" ? "Edit correction zones" : "Edit zones",
      storedZoneEditorDescription: "Drag zones on the floorplan or move their points. Double-click a detection-zone edge to add a point.",
      storedAddZone: "Add zone",
      storedNoZones: (source) => source === "calibration" ? "No correction zones are registered." : "No zones are registered.",
      storedZonePointCount: (count) => `${count}-point polygon`,
      storedExitLineShape: "Exit line",
      storedRectShape: "Rectangle",
      storedZoneName: "Zone name",
      storedZoneNamePlaceholder: "e.g. Bed, Desk",
      storedDetection: "Detection",
      storedFilter: "Exclusion",
      storedDisabled: "Disabled",
      storedExit: "Exit",
      storedConvertRect: "Convert to rectangle",
      storedDeletePoint: "Delete selected point",
      storedBlock: "Block",
      storedReduced: "Reduced sensitivity",
      storedOff: "Off",
      storedDeleteZone: "Delete selected zone",
      storedFurnitureTitle: "Furniture layout",
      storedFurnitureDescription: "Select furniture to add it to the center of the selected room. Drag it on the floorplan to adjust placement.",
      storedNoFurniture: "No furniture has been placed.",
      storedDeleteSelectedFurniture: "Delete selected furniture",
      storedRadarTitle: "Change radar position",
      storedRadarDescription: "Drag the radar body or move the rotation handle on the floorplan.",
      storedPreviewAria: "Saved floorplan preview",
      storedPreparingTitle: "Preparing floorplan data",
      storedPreparingDescription: "The image and configuration will appear here after loading.",
      storedDeleteTitle: "Delete saved floorplan",
      storedDeleteDescription: "All floorplan data will be deleted and cannot be restored. Continue?",
      storedDeleteNo: "No",
      storedDeleteYes: "Yes, delete it",
      storedDeleting: "Deleting",
      storedScaleSummary: (widthPx, heightPx, mmPerPxX, mmPerPxY) =>
        `Reference box ${widthPx.toFixed(2)} x ${heightPx.toFixed(2)}px · ${mmPerPxX.toFixed(2)} / ${mmPerPxY.toFixed(2)} mm/px`,
      storedOuterBoundsMissing: "Saved outer reference box could not be found.",
      storedRotateCounterClockwiseTitle: "Rotate selected furniture 90 degrees counterclockwise",
      storedRotateClockwiseTitle: "Rotate selected furniture 90 degrees clockwise",
      demoLivingRoom: "Living room",
      demoRoom: "Room"
    }
  }
};
